import { motion } from "framer-motion";
import type { IconType } from "react-icons";
import { LuBrainCircuit, LuZap, LuLockOpen, LuShieldCheck } from "react-icons/lu";

const VISION_PILLARS: { icon: IconType; label: string; desc: string; iconColor: string }[] = [
  {
    icon: LuBrainCircuit,
    label: "AI-driven decisions",
    desc: "Every recommendation backed by real financial data, not guesswork.",
    iconColor: "text-blue-500 dark:text-blue-300",
  },
  {
    icon: LuZap,
    label: "Instant intelligence",
    desc: "Real-time portfolio insights that update as your accounts change.",
    iconColor: "text-indigo-500 dark:text-indigo-300",
  },
  {
    icon: LuLockOpen,
    label: "Democratized access",
    desc: "Institutional-grade tools available to every individual investor.",
    iconColor: "text-cyan-500 dark:text-cyan-300",
  },
  {
    icon: LuShieldCheck,
    label: "Confident execution",
    desc: "Walk into every deal and lender meeting knowing your numbers cold.",
    iconColor: "text-emerald-500 dark:text-emerald-300",
  },
];

export function VisionSection() {
  return (
    <section className="relative px-4 sm:px-6 py-20 overflow-hidden">
      {/* Background — works in both modes */}
      <div className="absolute inset-0 dark:bg-gradient-to-b dark:from-vw-bg/60 dark:via-vw-bg/40 dark:to-vw-bg/60 bg-gradient-to-br from-blue-50 via-indigo-50/50 to-white pointer-events-none" />

      {/* Decorative glow orbs */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] dark:bg-blue-500/6 bg-blue-400/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] dark:bg-indigo-500/6 bg-indigo-300/10 rounded-full blur-[80px] pointer-events-none" />

      <div className="relative z-10 max-w-[1100px] mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-indigo-400 text-indigo-700 uppercase dark:bg-indigo-500/10 bg-indigo-100 rounded-full mb-4">
            Our Vision
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-5">
            A world where every investor{" "}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              invests with intelligence
            </span>
          </h2>
          <p className="text-base dark:text-slate-300 text-gray-600 max-w-2xl mx-auto leading-relaxed">
            We envision a world where AI-backed financial clarity isn't reserved for hedge funds and institutions. Where every real estate investor — whether they own one property or twenty — has the precision, confidence, and tools to make smarter decisions at every stage.
          </p>
        </div>

        {/* Pillars grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VISION_PILLARS.map((pillar, i) => (
            <motion.div
              key={pillar.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="dark:bg-white/[0.04] bg-white border dark:border-white/[0.08] border-gray-200 rounded-2xl p-6 text-center hover:-translate-y-1 hover:dark:bg-white/[0.07] hover:border-blue-300 dark:hover:border-blue-400/40 hover:shadow-lg dark:hover:shadow-blue-900/20 transition-all duration-300"
            >
              <div className="mb-4 flex justify-center">
                <pillar.icon className={`w-8 h-8 ${pillar.iconColor}`} />
              </div>
              <h3 className="text-sm font-bold dark:text-white text-gray-900 mb-2">{pillar.label}</h3>
              <p className="text-xs dark:text-slate-400 text-gray-500 leading-relaxed">{pillar.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Mission statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-12 text-center dark:bg-gradient-to-r dark:from-[#11245a] dark:to-[#0c1b47] bg-blue-50 border dark:border-blue-500/35 border-blue-300 rounded-2xl px-8 py-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-blue-900/20 hover:border-blue-400 dark:hover:border-blue-400/50"
        >
          <p className="text-lg dark:text-white text-gray-800 font-semibold">
            "Investment success through intelligence — not luck."
          </p>
          <p className="text-sm dark:text-slate-400 text-gray-500 mt-2">VaWeNa Mission Statement</p>
        </motion.div>
      </div>
    </section>
  );
}
